import * as THREE from 'three'
import type { GaussianSplatMaterial } from '@/plugins/gsplat/materials/GaussianSplatMaterial'
import { type ColorSpaceConfig, GammaMode, ToneMappingMode } from './ColorSpaceConstants'

/**
 * 色彩空间管理器
 * 对标 PlayCanvas 的 CameraShaderParams
 *
 * 负责：
 * 1. 检测渲染器色彩空间配置
 * 2. 自动配置材质的gamma/tonemapping
 * 3. 全局默认值管理
 *
 * 【与 Three.js 颜色管线的对齐说明】
 *
 * Three.js 标准材质使用 `linearToOutputTexel()` 进行颜色空间转换：
 * - 当 renderer.outputColorSpace = SRGBColorSpace 时：
 *   Three.js 会调用 sRGBTransferOETF()，将线性颜色编码为 sRGB
 *   → 此时 shader 应输出线性颜色，设置 GAMMA_NONE
 *
 * - 当 renderer.outputColorSpace = LinearSRGBColorSpace 时：
 *   Three.js 直接输出（不转换）
 *   → 如果需要 sRGB 输出，shader 需要自行编码，设置 GAMMA_SRGB
 *
 * 由于本项目使用 RawShaderMaterial（isRawShaderMaterial = true），
 * Three.js 不会自动注入 linearToOutputTexel 函数。
 * 因此通过 GAMMA_* 宏手动控制颜色空间转换。
 *
 * @example
 * ```typescript
 * const manager = new ColorSpaceManager(renderer);
 * manager.setDefaults({
 *   toneMapping: ToneMappingMode.ACES,
 *   exposure: 1.2
 * });
 * manager.configureMaterial(material);
 * ```
 */
export class ColorSpaceManager {
  private renderer: THREE.WebGLRenderer
  private _defaultConfig: ColorSpaceConfig = {
    gammaMode: 'auto',
    toneMapping: ToneMappingMode.NONE, // 修复：与 PlayCanvas 默认值一致
    exposure: 1.0,
  }

  /**
   * 构造函数
   * @param renderer Three.js WebGL渲染器
   */
  constructor(renderer: THREE.WebGLRenderer) {
    this.renderer = renderer
  }

  /**
   * 自动配置材质的色彩空间参数
   * 根据渲染目标和渲染器配置自动决定gamma模式
   *
   * 【重要说明】
   * 我们的 GaussianSplatMaterial 使用自定义 ShaderMaterial，
   * Three.js 不会自动注入 `#include <colorspace_fragment>` 和 `linearToOutputTexel()`。
   * 因此我们必须在 shader 中自己处理颜色空间转换。
   *
   * 【正确的逻辑】
   * - renderer.outputColorSpace = SRGBColorSpace 时：
   *   显示器需要 sRGB 颜色，但 Three.js 不会帮我们转换
   *   → shader 必须输出 sRGB，设置 GAMMA_SRGB
   *
   * - renderer.outputColorSpace = LinearSRGBColorSpace 时：
   *   显示器/后处理接收线性颜色
   *   → shader 输出线性，设置 GAMMA_NONE
   *
   * @param material 要配置的材质
   */
  configureMaterial(material: GaussianSplatMaterial): void {
    // 检测渲染目标
    const renderTarget = this.renderer.getRenderTarget()
    const isSRGBTarget = renderTarget?.texture.colorSpace === THREE.SRGBColorSpace

    // 检测渲染器输出色彩空间
    // Three.js 默认值：SRGBColorSpace（见 WebGLRenderer.js:276）
    const rendererOutputsSRGB = this.renderer.outputColorSpace === THREE.SRGBColorSpace

    // 决定gamma模式
    let gammaMode = this._defaultConfig.gammaMode
    if (gammaMode === 'auto') {
      // 自动检测：
      // 由于我们使用自定义 ShaderMaterial，Three.js 不会自动转换颜色空间
      // - 如果输出目标需要 sRGB，shader 必须自己编码 → GAMMA_SRGB
      // - 如果输出目标是线性，shader 输出线性 → GAMMA_NONE
      gammaMode = isSRGBTarget || rendererOutputsSRGB ? GammaMode.SRGB : GammaMode.NONE
    }

    // 应用配置到材质
    material.setGammaMode(gammaMode)
    material.setToneMapping(this._defaultConfig.toneMapping)
    material.setExposure(this._defaultConfig.exposure)
  }

  /**
   * 设置全局默认配置
   *
   * @param config 要更新的配置（支持部分更新）
   * @example
   * ```typescript
   * manager.setDefaults({
   *   toneMapping: ToneMappingMode.ACES,
   *   exposure: 1.5
   * });
   * ```
   */
  setDefaults(config: Partial<ColorSpaceConfig>): void {
    if (config.gammaMode !== undefined) {
      this._defaultConfig.gammaMode = config.gammaMode
    }
    if (config.toneMapping !== undefined) {
      this._defaultConfig.toneMapping = config.toneMapping
    }
    if (config.exposure !== undefined) {
      this._defaultConfig.exposure = config.exposure
    }
  }

  /**
   * 获取当前默认配置（只读）
   *
   * @returns 当前默认配置的副本
   */
  getDefaults(): Readonly<ColorSpaceConfig> {
    return { ...this._defaultConfig }
  }

  /**
   * 检测当前渲染环境的gamma需求
   *
   * @returns 检测到的gamma模式
   */
  detectGammaNeed(): GammaMode {
    const renderTarget = this.renderer.getRenderTarget()
    const isSRGBTarget = renderTarget?.texture.colorSpace === THREE.SRGBColorSpace
    const rendererOutputsSRGB = this.renderer.outputColorSpace === THREE.SRGBColorSpace

    // 修复：与 configureMaterial 逻辑一致
    // sRGB 输出需要 shader 进行 gamma 编码
    return isSRGBTarget || rendererOutputsSRGB ? GammaMode.SRGB : GammaMode.NONE
  }

  /**
   * 获取渲染器引用
   *
   * @returns 关联的WebGL渲染器
   */
  getRenderer(): THREE.WebGLRenderer {
    return this.renderer
  }
}
